import React, { useState, useMemo } from 'react'
import {
  UITable,
  UITableHeader,
  UITableBody,
  UITableFooter,
  UITableHead,
  UITableRow,
  UITableCell,
  UITableCaption,
  UITableProps
} from './ui-table'
import { UIButton } from '../buttons/ui-button'
import { UILabel } from '../forms/ui-label'
import { UITypography } from './ui-typography'
import { UIBox } from '../layout/ui-box'
import { cn } from '../../lib/utils'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

export interface UITablePaginatedColumn {
  key: string
  label: string | React.ReactNode
  align?: 'left' | 'center' | 'right'
  width?: string
  render?: (value: any, row: any, index?: number) => React.ReactNode
}

export interface UITablePaginatedData {
  [key: string]: any
}

export interface UITablePaginatedProps extends Omit<UITableProps, 'children'> {
  /**
   * 테이블 데이터
   */
  data: UITablePaginatedData[]
  /**
   * 테이블 컬럼 정의
   */
  columns: UITablePaginatedColumn[]
  /**
   * 페이지당 항목 수 (기본값: 10)
   */
  pageSize?: number
  /**
   * 초기 페이지 번호 (기본값: 1)
   */
  initialPage?: number
  /**
   * 표시할 페이지 번호 개수 (기본값: 5)
   */
  pageNumbersToShow?: number
  /**
   * 로딩 상태
   */
  loading?: boolean
  /**
   * 빈 상태 메시지
   */
  emptyMessage?: string
  /**
   * 테이블 캡션
   */
  caption?: string
  /**
   * 페이지 변경 콜백
   */
  onPageChange?: (page: number) => void
  /**
   * 페이지 크기 변경 콜백
   */
  onPageSizeChange?: (pageSize: number) => void
  /**
   * 페이지 크기 선택 옵션 표시 여부
   */
  showPageSizeSelector?: boolean
  /**
   * 사용 가능한 페이지 크기 옵션
   */
  pageSizeOptions?: number[]
  /**
   * 항목 수 표시 여부
   */
  showItemCount?: boolean
}

/**
 * Paginated 테이블 컴포넌트
 * UITable을 확장하여 페이지네이션 기능을 추가합니다.
 * 고급 페이지네이션 컨트롤을 제공하는 테이블 컴포넌트입니다.
 */
export const UITablePaginated = React.forwardRef<
  React.ComponentRef<typeof UITable>,
  UITablePaginatedProps
>((({
  data,
  columns,
  pageSize = 10,
  initialPage = 1,
  pageNumbersToShow = 5,
  loading = false,
  emptyMessage = 'No data available',
  caption,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = false,
  pageSizeOptions = [5, 10, 20, 50],
  showItemCount = true,
  className,
  ...props
}, ref) => {
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [currentPageSize, setCurrentPageSize] = useState(pageSize)

  // 페이지네이션 계산
  const totalPages = Math.ceil(data.length / currentPageSize)
  const startIndex = (currentPage - 1) * currentPageSize
  const endIndex = Math.min(startIndex + currentPageSize, data.length)

  // 현재 페이지 데이터
  const paginatedData = useMemo(() => {
    return data.slice(startIndex, endIndex)
  }, [data, startIndex, endIndex])

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      onPageChange?.(page)
    }
  }

  // 페이지 크기 변경 핸들러
  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageSize(newPageSize)
    setCurrentPage(1) // 페이지 크기 변경 시 첫 페이지로 이동
    onPageSizeChange?.(newPageSize)
  }

  // 페이지 번호 생성
  const getPageNumbers = () => {
    const pages: number[] = []
    const maxPages = Math.min(pageNumbersToShow, totalPages)

    let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2))
    let endPage = Math.min(totalPages, startPage + maxPages - 1)

    // 끝 페이지 조정
    if (endPage - startPage + 1 < maxPages) {
      startPage = Math.max(1, endPage - maxPages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return pages
  }

  return (
    <UIBox className={cn("w-full space-y-4", className)}>
      <UITable ref={ref} {...props}>
        {caption && <UITableCaption>{caption}</UITableCaption>}

        <UITableHeader>
          <UITableRow>
            {columns.map((column) => (
              <UITableHead
                key={column.key}
                className={cn(
                  column.align === 'center' && "text-center",
                  column.align === 'right' && "text-right"
                )}
                style={{ width: column.width }}
              >
                {column.label}
              </UITableHead>
            ))}
          </UITableRow>
        </UITableHeader>

        <UITableBody>
          {loading ? (
            <UITableRow>
              <UITableCell colSpan={columns.length} className="h-24 text-center">
                <UIBox className="flex items-center justify-center gap-2">
                  <UIBox className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-r-transparent" />
                  Loading...
                </UIBox>
              </UITableCell>
            </UITableRow>
          ) : paginatedData.length === 0 ? (
            <UITableRow>
              <UITableCell colSpan={columns.length} className="h-24 text-center text-foreground/70">
                {emptyMessage}
              </UITableCell>
            </UITableRow>
          ) : (
            paginatedData.map((row, index) => (
              <UITableRow key={startIndex + index}>
                {columns.map((column) => (
                  <UITableCell
                    key={column.key}
                    className={cn(
                      column.align === 'center' && "text-center",
                      column.align === 'right' && "text-right"
                    )}
                  >
                    {column.render ? column.render(row[column.key], row, startIndex + index) : row[column.key]}
                  </UITableCell>
                ))}
              </UITableRow>
            ))
          )}
        </UITableBody>
      </UITable>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <UIBox className="flex items-center justify-between gap-4">
          {/* Item Count */}
          {showItemCount && (
            <UITypography variant="body2" color="muted">
              Showing {startIndex + 1} to {endIndex} of {data.length} entries
            </UITypography>
          )}

          {/* Page Size Selector */}
          {showPageSizeSelector && (
            <UIBox className="flex items-center gap-2">
              <UILabel htmlFor="page-size-selector">
                <UITypography variant="body2" color="muted">Show</UITypography>
              </UILabel>
              <select
                id="page-size-selector"
                value={currentPageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                aria-label="페이지당 항목 수 선택"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <UITypography variant="body2" color="muted">entries</UITypography>
            </UIBox>
          )}

          {/* Page Navigation */}
          <nav role="navigation" aria-label="테이블 페이지네이션">
            <UIBox className="flex items-center gap-1">
              {/* First Page */}
              <UIButton
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
                aria-label="첫 번째 페이지로 이동"
              >
                <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
              </UIButton>

              {/* Previous Page */}
              <UIButton
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
                aria-label="이전 페이지로 이동"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </UIButton>

              {/* Page Numbers */}
              {getPageNumbers().map((pageNumber) => (
                <UIButton
                  key={pageNumber}
                  variant={currentPage === pageNumber ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNumber)}
                  className="h-8 w-8 p-0"
                  aria-label={`페이지 ${pageNumber}${currentPage === pageNumber ? ' (현재 페이지)' : '로 이동'}`}
                  aria-current={currentPage === pageNumber ? "page" : undefined}
                >
                  {pageNumber}
                </UIButton>
              ))}

              {/* Next Page */}
              <UIButton
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
                aria-label="다음 페이지로 이동"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </UIButton>

              {/* Last Page */}
              <UIButton
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
                aria-label="마지막 페이지로 이동"
              >
                <ChevronsRight className="h-4 w-4" aria-hidden="true" />
              </UIButton>
            </UIBox>
          </nav>
        </UIBox>
      )}
    </UIBox>
  )
}))

UITablePaginated.displayName = "UITablePaginated"